import { traduzirErroSupabase } from '@octawash/shared';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * Calcula força de senha (0..4) — mesma heuristica do signup-page pra consistência.
 * Replicada aqui pra evitar dep cruzada entre páginas auth.
 */
function calcularForcaSenha(senha: string): { score: number; label: string; cor: string } {
  let score = 0;
  if (senha.length >= 10) score++;
  if (senha.length >= 14) score++;
  if (/[A-Z]/.test(senha) && /[a-z]/.test(senha)) score++;
  if (/\d/.test(senha)) score++;
  if (/[^A-Za-z0-9]/.test(senha)) score++;
  const final = Math.min(score, 4);
  const meta = [
    { label: 'Muito fraca', cor: 'bg-rose-500' },
    { label: 'Fraca', cor: 'bg-rose-400' },
    { label: 'Razoável', cor: 'bg-amber-400' },
    { label: 'Boa', cor: 'bg-emerald-400' },
    { label: 'Excelente', cor: 'bg-emerald-500' },
  ];
  return { score: final, ...meta[final]! };
}

/**
 * RedefinirSenhaPage — alvo do link de recuperação enviado por email.
 *
 * Supabase: ao clicar no link, o usuário cai aqui com session ativa (recovery flow).
 * Apenas chamamos `supabase.auth.updateUser({ password })` — não precisa token manual.
 *
 * Idioma visual: idêntico ao auth premium (bg-octa-hero + blobs + glass card).
 * Inputs: nova senha + confirmar. Indicador de força reaproveitado do signup.
 * Sucesso celebratório com CheckCircle2 gigante + auto-redirect para login em 3s.
 */
export function RedefinirSenhaPage() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [senha, setSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [sessaoValida, setSessaoValida] = useState<boolean | null>(null);

  // Valida que ha uma sessão recovery ativa (link do email ainda valido)
  useEffect(() => {
    let cancelado = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelado) return;
      setSessaoValida(!!data.session);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const forca = useMemo(() => calcularForcaSenha(senha), [senha]);
  const senhasBatem = senha.length > 0 && senha === confirmar;
  const podeEnviar = senha.length >= 10 && senhasBatem && !carregando;

  async function redefinir(e: React.FormEvent) {
    e.preventDefault();
    if (senha.length < 10) {
      toast.error('Senha deve ter ao menos 10 caracteres');
      return;
    }
    if (senha !== confirmar) {
      toast.error('Senhas não coincidem');
      return;
    }
    setCarregando(true);
    const { error } = await supabase.auth.updateUser({ password: senha });
    setCarregando(false);
    if (error) {
      toast.error('Falha ao redefinir senha', { description: traduzirErroSupabase(error) });
      return;
    }
    setSucesso(true);
    // Desloga sessão recovery e manda pra login (Supabase recovery sessions
    // sao restritas — melhor login fresco depois de mudar senha)
    setTimeout(() => {
      void supabase.auth.signOut();
      navigate('/login', { replace: true });
    }, 2200);
  }

  const nomeMarca = tenant?.nom_tenant ?? 'Octa Pontos';
  const iniciais = tenant?.nom_tenant ? nomeMarca.slice(0, 2).toUpperCase() : 'OW';

  // Tela de sucesso celebratória
  if (sucesso) {
    return (
      <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
          style={{ animationDelay: '-4s' }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.15 }}
        />
        <motion.div
          className="relative z-10 w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <motion.div
            className="bg-octa-gradient shadow-octa-glow octa-confetti mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-4 ring-white/40"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <CheckCircle2 className="h-12 w-12 text-white" />
          </motion.div>
          <motion.h1
            className="text-display-md mt-6 font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Senha atualizada
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-3 text-sm"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            Agora é só entrar com a senha nova.
          </motion.p>
          <motion.div
            className="mt-6 flex items-center justify-center gap-2 text-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
          >
            <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
            <span className="text-muted-foreground">Redirecionando para o login…</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Sessão recovery invalida / expirada — mensagem amigável + link pra recomeçar
  if (sessaoValida === false) {
    return (
      <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.4, scale: 1 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          className="relative z-10 w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div className="bg-muted shadow-octa-lg mx-auto flex h-20 w-20 items-center justify-center rounded-full ring-4 ring-white/40">
            <KeyRound className="text-muted-foreground h-10 w-10" />
          </div>
          <h1 className="text-display-md mt-5 font-extrabold tracking-tight">Link expirado</h1>
          <p className="text-muted-foreground mt-3 px-2 text-sm">
            Esse link de recuperação não vale mais. Peça um novo em segundos.
          </p>
          <div className="mt-6">
            <Button
              asChild
              size="lg"
              variant="accent"
              className="group h-12 rounded-xl text-base font-bold"
            >
              <a href="/recuperar-senha">
                Pedir novo link
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Blobs morphing — mesma estética do login/signup */}
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
        style={{ animationDelay: '-4s' }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob pointer-events-none absolute right-1/4 top-1/4 h-72 w-72 bg-violet-400"
        style={{ animationDelay: '-8s', opacity: 0.25 }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.25, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
      />

      <div className="relative z-10 w-full max-w-sm space-y-6">
        {/* Logo + titulo */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.85, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        >
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={nomeMarca}
              className="shadow-octa-glow octa-float mx-auto mb-4 h-20 w-20 rounded-3xl object-cover ring-4 ring-white/40"
            />
          ) : (
            <div className="bg-octa-gradient shadow-octa-glow octa-float mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl ring-4 ring-white/40">
              <span className="text-2xl font-bold text-white">{iniciais}</span>
            </div>
          )}
          <motion.div
            className="octa-pastel-brand mx-auto mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <ShieldCheck className="h-3 w-3" />
            Crie uma senha forte
          </motion.div>
          <motion.h1
            className="text-display-md font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Nova senha
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-1.5 px-2 text-sm"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            Escolha algo memorável pra você e difícil pra qualquer outro.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="glass" className="shadow-octa-lg rounded-3xl border-white/40">
            <CardContent className="space-y-5 p-6">
              <form onSubmit={redefinir} className="octa-stagger space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="senha" className="text-xs font-semibold uppercase tracking-wider">
                    Nova senha
                  </Label>
                  <div className="relative">
                    <Lock
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="senha"
                      type={mostrarSenha ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      minLength={10}
                      autoComplete="new-password"
                      placeholder="Mínimo 10 caracteres"
                      className="h-11 pl-9 pr-10"
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- usuario chegou via link de reset, foco no campo unico melhora UX
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha((v) => !v)}
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Indicador de força (mesmo formato do signup) */}
                  <AnimatePresence>
                    {senha.length > 0 ? (
                      <motion.div
                        className="space-y-1.5 pt-1"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <div className="flex gap-1">
                          {[0, 1, 2, 3].map((i) => (
                            <motion.div
                              key={i}
                              className={`h-1 flex-1 rounded-full transition-colors ${
                                i < forca.score ? forca.cor : 'bg-muted'
                              }`}
                              initial={{ scaleX: 0 }}
                              animate={{ scaleX: 1 }}
                              transition={{ delay: i * 0.05 }}
                              style={{ transformOrigin: 'left' }}
                            />
                          ))}
                        </div>
                        <p className="text-muted-foreground text-[11px] font-medium">
                          Força: <span className="text-foreground">{forca.label}</span>
                        </p>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="confirmar"
                    className="text-xs font-semibold uppercase tracking-wider"
                  >
                    Confirmar senha
                  </Label>
                  <div className="relative">
                    <Lock
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="confirmar"
                      type={mostrarSenha ? 'text' : 'password'}
                      value={confirmar}
                      onChange={(e) => setConfirmar(e.target.value)}
                      required
                      minLength={10}
                      autoComplete="new-password"
                      placeholder="Digite novamente"
                      className="h-11 pl-9 pr-10"
                    />
                    {confirmar.length > 0 && (
                      <span
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                          senhasBatem ? 'text-emerald-500' : 'text-muted-foreground'
                        }`}
                      >
                        {senhasBatem ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <span className="text-[10px] font-semibold">…</span>
                        )}
                      </span>
                    )}
                  </div>
                  <AnimatePresence>
                    {confirmar.length > 0 && !senhasBatem ? (
                      <motion.p
                        className="pt-0.5 text-[11px] font-medium text-rose-500"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                      >
                        As senhas não coincidem
                      </motion.p>
                    ) : null}
                  </AnimatePresence>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  variant="accent"
                  disabled={!podeEnviar}
                  className="group h-12 w-full rounded-xl text-base font-bold"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {carregando ? (
                      <motion.span
                        key="loading"
                        className="inline-flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Salvando…
                      </motion.span>
                    ) : (
                      <motion.span
                        key="idle"
                        className="inline-flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        Atualizar senha
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          className="text-muted-foreground text-center text-[11px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75, duration: 0.5 }}
        >
          Você sairá automaticamente após salvar — entre de novo com a senha nova.
        </motion.p>
      </div>
    </div>
  );
}
